import React, { useEffect, useState } from 'react';
import { Button, Col, Image, Row, Stack } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from '../../assets/images/logo-white.svg';
import CommonFormikComponent from '../../components/common/CommonFormikComponent';
import CustomInputText from '../../components/common/CustomInputText';
import Loader from '../../components/common/loader';
import storage from '../../helpers/storage';
import { forgotPasswordAPI } from '../../services/auth.service';
import { ForgotSchema } from './validation';
import { redirectionRoutes } from '../../routes/redirectionRoutes';

/**
 * Form values type definition.
 */
type FormValues = {
    email: string;
}

export default function ForgotPassword() {
    const AppName = process.env.REACT_APP_NAME;

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    /**
     * Initial Form Values
     */
    const initialValues: FormValues = {
        email: "",
    };

    /**
     * Hook to check if user is already login 
     */
    useEffect(() => {
        if (storage.getToken() != null) {
            return navigate('/home')
        }
    }, [navigate])

    /**
     * Submit Method to call when user clicks on forgot button
     * @async
     * @param {FormValues} values
     * @param {any} actions
     * @returns {void}
     */
    const handleSubmit = async (values: Record<string, any>, actions: any) => {
        setLoading(true);
        actions.setSubmitting(true);
        const formData: any = {
            email: values.email
        };

        forgotPasswordAPI(formData).then((response: any) => {
            setLoading(false);
            actions.setSubmitting(false);
            toast(response.message, { type: toast.TYPE.SUCCESS });
            actions.resetForm(initialValues);
            navigate(redirectionRoutes.login.path)
        }).catch((error: any) => {
            setLoading(false);
            actions.setSubmitting(false);
            toast(error.data.message, { type: toast.TYPE.ERROR });
        })
    }

    return (
        <React.Fragment>
            <Loader isLoading={loading} />
            <Row className="g-0 vh-100">
                <Col
                    md={6}
                >
                    <Stack className='custom-primary-gradient text-center justify-content-around h-100 p-4' gap={4}>
                        <div className='user-select-none'>
                            <Image
                                fluid={true}
                                src={LOGO_WHITE}
                                alt={`${AppName} Logo`}
                                width={240}
                                height={80}
                            />
                        </div>
                        <div>
                            <Image
                                fluid={true}
                                src={LOGIN_BANNER}
                                alt={`${AppName}`}
                                width={500}
                                height={334}
                            />
                        </div>
                        <div className='lh-sm text-white'>
                            <div className='fw-light fs-4'>AI-Powered</div>
                            <div className='fw-semibold fs-1 text-uppercase'>Penetration Testing</div>
                            <div className='fw-light fs-4'>for Enterprises</div>
                        </div>
                    </Stack>
                </Col>
                <Col md={6} className="col-md-6 d-flex flex-column p-3">
                    <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
                        <div className='my-auto py-4'>
                            <h1 className="fw-semibold mb-1 fs-2">Forgot Password</h1>
                            <p className="text-secondary mb-4 pb-1">Please enter your registered email address</p>
                            <CommonFormikComponent
                                validationSchema={ForgotSchema}
                                initialValues={initialValues}
                                onSubmit={handleSubmit}
                            >
                                {(formikProps) => (
                                    <>
                                        <CustomInputText
                                            wrapperClassname="mb-3 pb-2"
                                            controlId="email"
                                            label='Email Address'
                                            placeholder="john.smith@example.com"
                                            touched={formikProps.touched.email}
                                            handleBlur={formikProps.handleBlur}
                                            handleChange={formikProps.handleChange}
                                            errorsField={formikProps.errors.email}
                                            value={formikProps.values.email}
                                            validationSchema={ForgotSchema}
                                        />

                                        <Stack direction="horizontal" gap={3} className="justify-content-end">
                                            <Button className="w-100 rounded-pill" variant="primary" type="submit" disabled={formikProps.isSubmitting || loading}
                                                    style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                                                {loading ? 'Processing...' : 'Submit'}
                                            </Button>
                                        </Stack>
                                    </>
                                )}
                            </CommonFormikComponent>
                        </div>
                        <div className='bg-dark bg-opacity-05 custom-min-height-40 fs-13 p-2 rounded text-center w-100 d-inline-flex align-items-center justify-content-center'>
                            <span className='pe-1'>Back to </span> <Link className="fw-semibold text-decoration-none" to={redirectionRoutes.login.path}>Login</Link>
                        </div>
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    )
}